import { useState, useEffect, useCallback } from 'react';
import { Button, Input, Badge } from '@jaylabs/ui';
import { useAuth } from '../context/AuthContext';
import './ChatSidebar.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function ChatSidebar({ isOpen, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);

  const fetchFriends = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/friends`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        const normalized = (data.friends || []).map(f => ({
          id: f.userId,
          username: f.username,
          displayName: f.displayName,
          email: f.email,
          totalPoints: f.totalPoints,
          level: f.level
        }));
        setFriends(normalized);
      }
    } catch (err) {
      console.error('Error fetching friends:', err);
    }
  }, []);

  const fetchMessages = useCallback(async (friendId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversation/${friendId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchFriends();
      if (selectedFriend) {
        fetchMessages(selectedFriend.id);
      }
    }
  }, [isOpen, selectedFriend, fetchFriends, fetchMessages]);

  useEffect(() => {
    if (!selectedFriend || !isOpen) return;

    fetchMessages(selectedFriend.id);

    let intervalId = null;

    const startPolling = () => {
      clearInterval(intervalId);
      const delay = document.visibilityState === 'hidden' ? 30000 : 5000;
      intervalId = setInterval(() => fetchMessages(selectedFriend.id), delay);
    };

    startPolling();
    document.addEventListener('visibilitychange', startPolling);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', startPolling);
    };
  }, [selectedFriend, isOpen, fetchMessages]);

  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || !selectedFriend) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipientUserId: selectedFriend.id,
          messageText: newMessage,
          messageType: 'general'
        })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(selectedFriend.id);
      }
    } catch (err) {
      console.error('Error sending message:', err);
    }
  }, [newMessage, selectedFriend, fetchMessages]);

  if (!isOpen) return null;

  return (
    <div className="dd-chat-sidebar">
      {/* Header */}
      <div className="dd-chat-header">
        <h3 className="dd-heading-md">💬 Chat</h3>
        <button className="dd-icon-btn" onClick={onClose} aria-label="Close chat">
          ✕
        </button>
      </div>

      {/* Friends List or Chat */}
      {!selectedFriend ? (
        <div className="dd-chat-body dd-vstack dd-gap-2">
          <p className="dd-text-sm dd-text-secondary dd-mb-2">
            Select a friend to chat with:
          </p>
          {friends.length === 0 ? (
            <p className="dd-text-sm dd-text-secondary dd-text-center dd-py-4">
              No friends yet. Add friends to start chatting!
            </p>
          ) : (
            friends.map(friend => (
              <div
                key={friend.id}
                className="dd-chat-friend-item"
                onClick={() => setSelectedFriend(friend)}
              >
                <span className="dd-text-semibold">
                  {friend.displayName || friend.username || friend.email}
                </span>
                <Badge variant="success" size="sm">Online</Badge>
              </div>
            ))
          )}
        </div>
      ) : (
        <>
          {/* Chat Header */}
          <div className="dd-chat-subheader">
            <Button variant="ghost" size="sm" onClick={() => setSelectedFriend(null)}>
              ← Back
            </Button>
            <span className="dd-text-bold dd-flex-1">
              {selectedFriend.displayName || selectedFriend.username || selectedFriend.email}
            </span>
          </div>

          {/* Messages */}
          <div className="dd-chat-body dd-vstack dd-gap-3">
            {loading ? (
              <div className="dd-spinner-center">
                <div className="dd-spinner"></div>
              </div>
            ) : messages.length === 0 ? (
              <p className="dd-text-sm dd-text-secondary dd-text-center dd-py-4">
                No messages yet. Start the conversation!
              </p>
            ) : (
              messages.map((msg, idx) => {
                const senderId = msg.sender_user_id ?? msg.sender_id;
                const isSender = senderId === user.id;
                return (
                  <div
                    key={idx}
                    className={`dd-chat-bubble-wrap ${isSender ? 'dd-chat-bubble-sent' : 'dd-chat-bubble-received'}`}
                  >
                    <div className={`dd-chat-bubble ${isSender ? 'dd-chat-bubble-mine' : 'dd-chat-bubble-theirs'}`}>
                      <p className="dd-text-sm">
                        {msg.message_text || msg.message || ''}
                      </p>
                      <p className="dd-chat-time">
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Input */}
          <div className="dd-chat-input-bar">
            <Input
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              size="md"
            />
            <Button variant="primary" onClick={sendMessage}>
              Send
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default ChatSidebar;
